import { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import Editor, { type Monaco } from '@monaco-editor/react'
import Logo from '../components/Logo'
import AuthHeader from '../components/AuthHeader'
import QDayCountdown from '../components/QDayCountdown'
import {
  resourceFormalApi,
  type RFFramework,
  type RFGap,
  type RFStatus,
  type RFSource,
  type RFDeps,
  type RFLayer,
  type RFSysLayer,
  type RFVerifiers,
  type RFDeclaration,
  type RFStatusCounts,
} from '../utils/api'

// ============================================================================
// Lean 4 syntax for Monaco
// ============================================================================

function registerLean4(monaco: Monaco) {
  if (monaco.languages.getLanguages().some((l: { id: string }) => l.id === 'lean4')) return
  monaco.languages.register({ id: 'lean4', extensions: ['.lean'] })
  monaco.languages.setMonarchTokensProvider('lean4', {
    defaultToken: '',
    keywords: [
      'theorem', 'lemma', 'def', 'abbrev', 'example', 'axiom', 'structure',
      'inductive', 'class', 'instance', 'namespace', 'end', 'open', 'import',
      'variable', 'variables', 'section', 'where', 'with', 'in', 'let', 'have',
      'show', 'fun', 'λ', 'if', 'then', 'else', 'match', 'do', 'return',
      'by', 'apply', 'exact', 'intro', 'intros', 'rfl', 'simp', 'rw', 'decide',
      'native_decide', 'sorry', 'admit', 'constructor', 'cases', 'induction',
      'noncomputable', 'protected', 'private', 'mutual', 'partial',
      'deriving', 'derive', 'attribute', 'macro', 'macro_rules', 'syntax',
      'elab', 'def_rules', 'notation', 'prefix', 'infix', 'infixl', 'infixr',
      'postfix',
    ],
    symbols: /[=><!~?:&|+\-*\/\^%]+/,
    tokenizer: {
      root: [
        [/\/-/, 'comment', '@blockComment'],
        [/--.*$/, 'comment'],
        [/\b(theorem|lemma|def|abbrev|axiom|structure|inductive|class|instance|example)\b/, 'keyword.declaration'],
        [/\b(by|apply|exact|intro|intros|rfl|simp|rw|decide|native_decide|sorry|constructor|cases|induction|have|show|fun)\b/, 'keyword'],
        [/\b(import|namespace|end|open|variable|section)\b/, 'keyword.module'],
        [/\b(Nat|Int|Bool|String|List|Fin|Prop|Type|Sort|Option|Real|Matrix|Set)\b/, 'type'],
        [/[A-Z][\w']*/, 'type'],
        [/[a-z_][\w']*/, 'identifier'],
        [/"([^"\\]|\\.)*"/, 'string'],
        [/\d+/, 'number'],
        [/@symbols/, 'operator'],
        [/[(){}\[\]]/, '@brackets'],
      ],
      blockComment: [
        [/-\//, 'comment', '@pop'],
        [/[^-]+/, 'comment'],
        [/-/, 'comment'],
      ],
    },
  })
}

// ============================================================================
// Helpers
// ============================================================================

const STATUS_COLOR = {
  verified: '#10b981',
  scaffolded: '#f59e0b',
  axiom: '#ef4444',
  structural: '#6366f1',
}

function MiniStatusBar({ counts }: { counts: RFStatusCounts }) {
  const total = counts.total || 1
  const segs = [
    { v: counts.verified, c: STATUS_COLOR.verified },
    { v: counts.scaffolded, c: STATUS_COLOR.scaffolded },
    { v: counts.axiom, c: STATUS_COLOR.axiom },
    { v: counts.structural, c: STATUS_COLOR.structural },
  ]
  return (
    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden flex">
      {segs.map((s, i) => s.v > 0 && (
        <div key={i} style={{ width: `${(s.v / total) * 100}%`, backgroundColor: s.c }} />
      ))}
    </div>
  )
}

// ============================================================================
// Framework diagram — structural, minimal text
// ============================================================================

type NodeClick = (file: string, line?: number) => void

function FrameworkDiagram({ framework, onOpen }: { framework: RFFramework, onOpen: NodeClick }) {
  return (
    <section className="bg-white rounded-2xl border border-qcloud-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-qcloud-text">Framework architecture</h2>
        <Link to="/resource-verification/graph" className="text-sm text-qcloud-primary hover:text-qcloud-secondary font-medium">
          Full dependency graph →
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className="lg:col-span-5 space-y-2">
          {framework.layers.map((layer, i) => <LayerChip key={layer.id} layer={layer} onOpen={onOpen} accent={i} />)}
        </div>
        <div className="lg:col-span-4">
          {framework.syslayer && <SysLayerChip sys={framework.syslayer} onOpen={onOpen} />}
        </div>
        <div className="lg:col-span-3">
          {framework.verifiers && <VerifierChip ver={framework.verifiers} onOpen={onOpen} />}
        </div>
      </div>
    </section>
  )
}

const LAYER_ACCENT = [
  'border-rose-200    bg-rose-50/60',
  'border-amber-200   bg-amber-50/60',
  'border-emerald-200 bg-emerald-50/60',
  'border-sky-200     bg-sky-50/60',
]

function LayerChip({ layer, onOpen, accent }: { layer: RFLayer, onOpen: NodeClick, accent: number }) {
  return (
    <div className={`rounded-lg border ${LAYER_ACCENT[accent % 4]} p-3`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="font-bold text-sm text-qcloud-text">{layer.name}</div>
        <div className="text-[10px] text-qcloud-muted font-mono">
          {layer.status_counts.verified}✓
          {layer.status_counts.scaffolded > 0 && <span className="text-amber-600 ml-1">{layer.status_counts.scaffolded}⚠</span>}
          {layer.status_counts.axiom > 0 && <span className="text-rose-600 ml-1">{layer.status_counts.axiom}!</span>}
        </div>
      </div>
      <MiniStatusBar counts={layer.status_counts} />
      <div className="mt-2 flex flex-wrap gap-1">
        {layer.key_decls.slice(0, 3).map(d => (
          <button
            key={d.name}
            onClick={() => onOpen(d.file_path, d.line)}
            className="text-[10.5px] px-1.5 py-0.5 rounded font-mono bg-white border border-slate-200 hover:border-qcloud-primary hover:text-qcloud-primary transition-colors"
            title={`${d.kind} · ${d.file_path}:${d.line}`}
          >
            {d.name}
          </button>
        ))}
      </div>
    </div>
  )
}

function SysLayerChip({ sys, onOpen }: { sys: RFSysLayer, onOpen: NodeClick }) {
  return (
    <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/50 p-3 h-full">
      <div className="font-bold text-sm text-indigo-900 mb-2">SysLayer · Runtime invariants</div>

      <div className="grid grid-cols-2 gap-1 mb-3">
        {sys.invariants.map(inv => (
          <button
            key={inv.id}
            onClick={() => inv.decl && onOpen(inv.decl.file_path, inv.decl.line)}
            className="text-left px-2 py-1 rounded bg-white border border-indigo-100 hover:border-indigo-400 transition-colors"
            title={inv.description}
          >
            <div className="text-[9.5px] font-bold text-indigo-700">{inv.id}</div>
            <div className="text-[10.5px] font-semibold text-qcloud-text leading-tight">{inv.name}</div>
          </button>
        ))}
      </div>

      <div className="text-[9px] uppercase tracking-wider text-indigo-700 font-bold mb-1">SysCalls (8)</div>
      <div className="grid grid-cols-2 gap-0.5 mb-3">
        {sys.syscalls.map(sc => (
          <div
            key={sc.name}
            className="text-[10px] font-mono text-indigo-900 px-1.5 py-0.5 rounded bg-white border border-indigo-100"
            title={sc.desc}
          >
            {sc.name}
          </div>
        ))}
      </div>

      <div className="text-[9px] uppercase tracking-wider text-indigo-700 font-bold mb-1">Zone roles</div>
      <div className="flex flex-wrap gap-0.5">
        {sys.zone_roles.map(r => (
          <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-medium">{r}</span>
        ))}
      </div>
    </div>
  )
}

function VerifierChip({ ver, onOpen }: { ver: RFVerifiers, onOpen: NodeClick }) {
  return (
    <div className="rounded-lg border-2 border-purple-200 bg-purple-50/50 p-3 h-full">
      <div className="font-bold text-sm text-purple-900 mb-2">Implementer / Verifier</div>
      <div className="space-y-1">
        {ver.actors.map((actor, i) => (
          <div key={actor.id}>
            <button
              onClick={() => actor.decl && onOpen(actor.decl.file_path, actor.decl.line)}
              className="block w-full text-left px-2 py-1 rounded bg-white border border-purple-100 hover:border-purple-400 transition-colors"
              title={actor.description}
            >
              <div className="flex items-center gap-1.5">
                <span className={`text-[9px] uppercase font-bold ${i === 0 ? 'text-purple-700' : 'text-purple-500'}`}>
                  {i === 0 ? 'in' : '✓'}
                </span>
                <span className="text-[11px] font-semibold text-qcloud-text">{actor.name}</span>
              </div>
            </button>
            {i < ver.actors.length - 1 && <div className="text-purple-300 text-center text-[8px] leading-3">↓</div>}
          </div>
        ))}
      </div>
      <div className="mt-2 text-[9.5px] text-purple-800 italic text-center">
        all three pass ∧ semantic check
      </div>
    </div>
  )
}

// ============================================================================
// Hardware Abstraction Diagram — the novel cross-hardware contribution
// ============================================================================

function HardwareAbstractionDiagram() {
  return (
    <section className="bg-white rounded-2xl border border-qcloud-border p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-qcloud-text">One abstraction, four hardware families</h2>
        <p className="text-sm text-qcloud-muted">
          Implementers schedule explicit SysCalls — including decoder and Pauli-frame steps — on the same Zone/Channel substrate, regardless of platform.
        </p>
      </div>

      <svg viewBox="0 0 920 380" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="hwGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.18" />
          </linearGradient>
          <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" fill="#94a3b8" />
          </marker>
        </defs>

        {/* Three hardware platforms, top row */}
        {[
          { x: 60,  name: 'Neutral atom',     transport: 'AOD reconfig',  cite: 'Bluvstein 2024 · Cain–Xu 2026', color: '#10b981',
            icon: (<g><circle cx="20" cy="-8" r="3" fill="#10b981"/><circle cx="32" cy="-8" r="3" fill="#10b981"/><circle cx="44" cy="-8" r="3" fill="#10b981"/><circle cx="20" cy="4" r="3" fill="#10b981"/><circle cx="32" cy="4" r="3" fill="#10b981"/><circle cx="44" cy="4" r="3" fill="#10b981"/><path d="M28 -8 L36 -8 M28 4 L36 4" stroke="#10b981" strokeWidth="0.6" strokeDasharray="2 2"/></g>) },
          { x: 320, name: 'Superconducting',  transport: 'Swap network',  cite: 'IBM Eagle · Google Sycamore',  color: '#f59e0b',
            icon: (<g><rect x="18" y="-12" width="36" height="24" rx="3" fill="none" stroke="#f59e0b" strokeWidth="1.5"/><line x1="14" y1="-8" x2="18" y2="-8" stroke="#f59e0b" strokeWidth="1.2"/><line x1="14" y1="0" x2="18" y2="0" stroke="#f59e0b" strokeWidth="1.2"/><line x1="14" y1="8" x2="18" y2="8" stroke="#f59e0b" strokeWidth="1.2"/><line x1="54" y1="-8" x2="58" y2="-8" stroke="#f59e0b" strokeWidth="1.2"/><line x1="54" y1="0" x2="58" y2="0" stroke="#f59e0b" strokeWidth="1.2"/><line x1="54" y1="8" x2="58" y2="8" stroke="#f59e0b" strokeWidth="1.2"/><circle cx="28" cy="0" r="2" fill="#f59e0b"/><circle cx="44" cy="0" r="2" fill="#f59e0b"/></g>) },
          { x: 580, name: 'Trapped ion',      transport: 'Ion shuttling', cite: 'Quantinuum H1 · Pino 2021',      color: '#ec4899',
            icon: (<g><line x1="14" y1="0" x2="58" y2="0" stroke="#ec4899" strokeWidth="1.2"/><circle cx="22" cy="0" r="3" fill="#ec4899"/><circle cx="32" cy="0" r="3" fill="#ec4899"/><circle cx="42" cy="0" r="3" fill="#ec4899"/><circle cx="52" cy="0" r="3" fill="#ec4899"/><path d="M14 -8 L14 8 M58 -8 L58 8" stroke="#ec4899" strokeWidth="1.5"/></g>) },
          { x: 770, name: 'Spin qubit',       transport: 'Charge shuttle',cite: 'Krantz 2019 (future)',           color: '#a855f7',
            icon: (<g><rect x="20" y="-8" width="10" height="16" rx="2" fill="none" stroke="#a855f7" strokeWidth="1.3"/><rect x="36" y="-8" width="10" height="16" rx="2" fill="none" stroke="#a855f7" strokeWidth="1.3"/><circle cx="25" cy="0" r="2" fill="#a855f7"/><circle cx="41" cy="0" r="2" fill="#a855f7"/><path d="M30 0 L36 0" stroke="#a855f7" strokeWidth="1" strokeDasharray="1.5 1.5"/></g>) },
        ].map(p => (
          <g key={p.name}>
            <rect x={p.x} y={20} width={140} height={80} rx={10} fill="white" stroke={p.color} strokeWidth={1.5} />
            <g transform={`translate(${p.x + 36}, ${50})`}>{p.icon}</g>
            <text x={p.x + 70} y={78} textAnchor="middle" fontSize={11.5} fontWeight={700} fill="#0f172a">{p.name}</text>
            <text x={p.x + 70} y={92} textAnchor="middle" fontSize={9.5} fill="#64748b">{p.transport}</text>
            {/* Arrow down to abstraction */}
            <line x1={p.x + 70} y1={100} x2={p.x + 70} y2={150} stroke="#94a3b8" strokeWidth={1.2} markerEnd="url(#arr)" />
          </g>
        ))}

        {/* Unified abstraction layer */}
        <rect x={50} y={160} width={870} height={108} rx={12} fill="url(#hwGrad)" stroke="#6366f1" strokeWidth={1.8} />
        <text x={70} y={184} fontSize={13} fontWeight={800} fill="#3730a3">Unified system abstraction</text>
        <text x={70} y={199} fontSize={10.5} fill="#475569">Zone · Channel · SysCall — same Lean theorems prove correctness across all platforms.</text>

        {/* Eight SysCall chips */}
        {[
          'Gate1q', 'Gate2q', 'Measure', 'TransitQubit',
          'RequestFreshAncilla', 'RequestMagicState', 'DecodeSyndrome', 'PauliFrameUpdate',
        ].map((sc, i) => {
          const col = i % 4, row = Math.floor(i / 4)
          const x = 70 + col * 210, y = 218 + row * 22
          const explicit = sc === 'DecodeSyndrome' || sc === 'PauliFrameUpdate'
          return (
            <g key={sc}>
              <rect x={x} y={y - 12} width={195} height={18} rx={4} fill={explicit ? '#fef3c7' : 'white'} stroke={explicit ? '#f59e0b' : '#cbd5e1'} strokeWidth={explicit ? 1.4 : 1} />
              <text x={x + 8} y={y} fontSize={10.5} fontFamily="ui-monospace, monospace" fontWeight={explicit ? 700 : 500} fill="#0f172a">{sc}</text>
              {explicit && <text x={x + 188} y={y} fontSize={9} textAnchor="end" fill="#b45309" fontWeight={600}>explicit</text>}
            </g>
          )
        })}

        {/* Arrow down to "what this gives you" */}
        <line x1={460} y1={268} x2={460} y2={302} stroke="#94a3b8" strokeWidth={1.2} markerEnd="url(#arr)" />

        {/* Bottom: design choice highlight */}
        <rect x={150} y={310} width={620} height={48} rx={10} fill="#f0fdf4" stroke="#10b981" strokeWidth={1.5} />
        <text x={460} y={331} textAnchor="middle" fontSize={11.5} fontWeight={700} fill="#065f46">
          Decoder calls, Pauli-frame updates, atom moves — all explicit in the schedule.
        </text>
        <text x={460} y={347} textAnchor="middle" fontSize={10.5} fill="#047857">
          Default pipelines hide them. We make them schedulable so capacity, latency, and throughput are statically verifiable.
        </text>
      </svg>
    </section>
  )
}

// ============================================================================
// Critical assumptions — categorized by closing path (math vs hardware),
// further grouped by sub-category within each.
// ============================================================================

const STATUS_LABEL = {
  verified:    'Closed',
  discrepancy: 'Open · counterexample',
  conditional: 'Open · conditional',
  stub:        'Open · needs construction',
  drafted:     'Open · in progress',
  partial:     'Open · partial',
  failed:      'Open',
  unknown:     'Open',
} as const

const STATUS_DOT = {
  verified:    'bg-emerald-500',
  discrepancy: 'bg-rose-500',
  conditional: 'bg-amber-500',
  stub:        'bg-sky-500',
  drafted:     'bg-violet-500',
  partial:     'bg-amber-500',
  failed:      'bg-rose-500',
  unknown:     'bg-slate-400',
} as const

const SUBCAT_ORDER = [
  'arithmetic',
  'comparison',
  'optimization',
  'ppm',
  'scheduling',
  'axioms',
  'transport',
  'replenishment',
]

const SUBCAT_ICON: Record<string, string> = {
  arithmetic:    '∑',
  comparison:    '⇆',
  optimization:  '✦',
  ppm:           '⌬',
  scheduling:    '⏱',
  axioms:        '⟦⟧',
  transport:     '⇢',
  replenishment: '⟲',
}

function groupGaps(gaps: RFGap[]) {
  const byCat: Record<'math' | 'hardware', Map<string, RFGap[]>> = {
    math: new Map(),
    hardware: new Map(),
  }
  for (const g of gaps) {
    const cat: 'math' | 'hardware' = g.category === 'hardware' ? 'hardware' : 'math'
    const sub = g.subcategory_id || 'other'
    if (!byCat[cat].has(sub)) byCat[cat].set(sub, [])
    byCat[cat].get(sub)!.push(g)
  }
  const sortSubs = (m: Map<string, RFGap[]>) => {
    const keys = Array.from(m.keys()).sort((a, b) => {
      const ia = SUBCAT_ORDER.indexOf(a), ib = SUBCAT_ORDER.indexOf(b)
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib)
    })
    return keys.map(k => ({ subId: k, items: m.get(k)! }))
  }
  return { math: sortSubs(byCat.math), hardware: sortSubs(byCat.hardware) }
}

function GapCard({ g, onOpen }: { g: RFGap, onOpen: NodeClick }) {
  const closed = g.status === 'verified'
  return (
    <button
      onClick={() => onOpen(g.file_path)}
      className={`text-left w-full px-3 py-2 rounded-lg border bg-white hover:border-qcloud-primary hover:shadow-sm transition-all ${
        closed ? 'border-emerald-200' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center gap-2 mb-0.5">
        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[g.status]}`} />
        <span className="font-bold text-[12px] font-mono text-qcloud-text">{g.id}</span>
        <span className={`ml-auto text-[9.5px] uppercase tracking-wider font-semibold ${closed ? 'text-emerald-700' : 'text-qcloud-muted'}`}>
          {STATUS_LABEL[g.status]}
        </span>
      </div>
      <div className="text-[12.5px] text-qcloud-text leading-snug line-clamp-2">{g.title.replace(/\*\*/g, '')}</div>
      {g.ratio && (
        <div className="text-[10.5px] font-mono text-qcloud-muted mt-0.5">{g.ratio.replace(/\*\*/g, '')}</div>
      )}
    </button>
  )
}

function SubcategoryGroup({ subId, items, onOpen, accent }: {
  subId: string, items: RFGap[], onOpen: NodeClick, accent: 'math' | 'hardware'
}) {
  if (items.length === 0) return null
  const subName = items[0].subcategory
  const invite = items[0].contribution_invite
  const accentColor = accent === 'math' ? 'text-indigo-700' : 'text-amber-700'
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <span className={`text-base font-mono ${accentColor}`}>{SUBCAT_ICON[subId] || '·'}</span>
        <h4 className="text-[12px] font-bold text-qcloud-text uppercase tracking-wider">{subName}</h4>
        <span className="text-[10px] text-qcloud-muted ml-auto font-mono">{items.length}</span>
      </div>
      {invite && (
        <div className="text-[10.5px] text-qcloud-muted italic mb-2 pl-6">→ {invite}</div>
      )}
      <div className="space-y-1.5 pl-6">
        {items.map(g => <GapCard key={g.id} g={g} onOpen={onOpen} />)}
      </div>
    </div>
  )
}

function CriticalAssumptions({ gaps, onOpen }: { gaps: RFGap[], onOpen: NodeClick }) {
  const groups = useMemo(() => groupGaps(gaps), [gaps])
  const closedCount = gaps.filter(g => g.status === 'verified').length
  const openCount = gaps.length - closedCount

  return (
    <section className="bg-white rounded-2xl border border-qcloud-border p-6">
      <div className="flex items-baseline gap-3 mb-1 flex-wrap">
        <h2 className="text-xl font-bold text-qcloud-text">Critical assumptions</h2>
        <span className="text-xs text-qcloud-muted">{openCount} open · {closedCount} closed</span>
      </div>
      <p className="text-sm text-qcloud-muted mb-5 max-w-3xl">
        Every assumption the audit surfaces is either provable in Lean or requires hardware evidence. Find the one you can close.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Math column */}
        <div className="rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-white p-4">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-indigo-500" />
            <h3 className="text-base font-bold text-indigo-900">Mathematically provable</h3>
            <span className="text-[11px] font-mono text-qcloud-muted ml-auto">
              {groups.math.reduce((n, g) => n + g.items.length, 0)} cards
            </span>
          </div>
          <p className="text-[11px] text-qcloud-muted mb-4 leading-relaxed">
            Closable by a Lean proof on top of the existing framework — submit a verified circuit, schedule, or derivation.
          </p>
          <div className="space-y-4">
            {groups.math.map(({ subId, items }) => (
              <SubcategoryGroup key={subId} subId={subId} items={items} onOpen={onOpen} accent="math" />
            ))}
          </div>
        </div>

        {/* Hardware column */}
        <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50/40 to-white p-4">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <h3 className="text-base font-bold text-amber-900">Hardware assumption</h3>
            <span className="text-[11px] font-mono text-qcloud-muted ml-auto">
              {groups.hardware.reduce((n, g) => n + g.items.length, 0)} cards
            </span>
          </div>
          <p className="text-[11px] text-qcloud-muted mb-4 leading-relaxed">
            Closable only by physical-realization evidence — measurements, calibration data, or hardware engineering the framework treats as input.
          </p>
          <div className="space-y-4">
            {groups.hardware.map(({ subId, items }) => (
              <SubcategoryGroup key={subId} subId={subId} items={items} onOpen={onOpen} accent="hardware" />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ============================================================================
// Lean source slide-over (Monaco)
// ============================================================================

function SourceSlideOver({
  open, source, deps, gotoLine, onClose,
}: {
  open: boolean
  source: RFSource | null
  deps: RFDeps | null
  gotoLine?: number | null
  onClose: () => void
}) {
  const [tab, setTab] = useState<'source' | 'deps' | 'decls'>('source')
  useEffect(() => { if (open) setTab('source') }, [open, source?.path])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full md:w-[78%] lg:w-[68%] xl:w-[60%] bg-white border-l border-qcloud-border shadow-2xl flex flex-col">
        <div className="px-4 py-2.5 border-b border-qcloud-border flex items-center gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-qcloud-text truncate">{source?.module || '—'}</div>
            <div className="text-[11px] text-qcloud-muted font-mono truncate">{source?.path || ''}</div>
          </div>
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {([['source', 'Source'], ['decls', 'Decls'], ['deps', 'Deps']] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${tab === k ? 'bg-white text-qcloud-text shadow-sm' : 'text-qcloud-muted hover:text-qcloud-text'}`}
              >
                {label}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="text-qcloud-muted hover:text-qcloud-text p-1" title="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          {!source ? (
            <div className="h-full flex items-center justify-center text-qcloud-muted">Loading…</div>
          ) : tab === 'source' ? (
            <Editor
              height="100%"
              defaultLanguage="lean4"
              language="lean4"
              theme="vs"
              value={source.content}
              options={{
                readOnly: true,
                minimap: { enabled: true, scale: 1 },
                fontSize: 13,
                lineNumbers: 'on',
                wordWrap: 'on',
                renderLineHighlight: 'line',
                automaticLayout: true,
                scrollBeyondLastLine: false,
              }}
              beforeMount={registerLean4}
              onMount={(editor) => {
                if (gotoLine) {
                  editor.revealLineInCenter(gotoLine)
                  editor.setPosition({ lineNumber: gotoLine, column: 1 })
                }
              }}
            />
          ) : tab === 'decls' ? (
            <DeclsList decls={source.declarations} />
          ) : (
            <DepsList deps={deps} />
          )}
        </div>
      </div>
    </div>
  )
}

function DeclsList({ decls }: { decls: RFDeclaration[] }) {
  const kindOrder = ['theorem', 'lemma', 'def', 'abbrev', 'structure', 'inductive', 'instance', 'axiom', 'example']
  const sorted = [...decls].sort((a, b) =>
    (kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind)) || a.name.localeCompare(b.name)
  )
  return (
    <div className="h-full overflow-y-auto p-4 space-y-1.5">
      {sorted.map((d, i) => (
        <div key={`${d.name}-${i}`} className="px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-qcloud-primary transition-colors">
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
              d.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
              d.status === 'scaffolded' ? 'bg-amber-100 text-amber-700' :
              d.status === 'axiom' ? 'bg-rose-100 text-rose-700' :
              'bg-slate-100 text-slate-600'
            }`}>{d.kind}</span>
            <span className="font-mono text-sm font-semibold text-qcloud-text">{d.name}</span>
            <span className="ml-auto text-[11px] text-qcloud-muted">L{d.line}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

function DepsList({ deps }: { deps: RFDeps | null }) {
  if (!deps) return <div className="h-full flex items-center justify-center text-qcloud-muted">Loading…</div>
  const focusNode = deps.nodes.find(n => n.path === deps.focus)
  if (!focusNode) return <div className="p-4 text-qcloud-muted">No dependency info available.</div>
  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-qcloud-muted mb-2">Imports ({focusNode.imports.length})</div>
        <div className="space-y-1">
          {focusNode.imports.length === 0
            ? <div className="text-xs text-qcloud-muted italic">No project-internal imports.</div>
            : focusNode.imports.map(p => (
                <div key={p} className="text-xs font-mono text-qcloud-text px-2 py-1 rounded bg-slate-50 border border-slate-200">{p}</div>
              ))}
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-qcloud-muted mb-2">Imported by ({focusNode.imported_by.length})</div>
        <div className="space-y-1">
          {focusNode.imported_by.length === 0
            ? <div className="text-xs text-qcloud-muted italic">No reverse imports.</div>
            : focusNode.imported_by.map(p => (
                <div key={p} className="text-xs font-mono text-qcloud-text px-2 py-1 rounded bg-slate-50 border border-slate-200">{p}</div>
              ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Main page
// ============================================================================

function ResourceVerificationPage() {
  const [status, setStatus] = useState<RFStatus | null>(null)
  const [framework, setFramework] = useState<RFFramework | null>(null)
  const [gaps, setGaps] = useState<RFGap[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [openFile, setOpenFile] = useState<string | null>(null)
  const [gotoLine, setGotoLine] = useState<number | null>(null)
  const [source, setSource] = useState<RFSource | null>(null)
  const [deps, setDeps] = useState<RFDeps | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      resourceFormalApi.getStatus(),
      resourceFormalApi.getFramework(),
      resourceFormalApi.getGaps(),
    ])
      .then(([st, fw, gp]) => {
        if (cancelled) return
        setStatus(st); setFramework(fw); setGaps(gp.entries)
      })
      .catch(err => { if (!cancelled) setError(err?.message || 'Failed to load') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const openSource = useCallback<NodeClick>((file, line) => {
    setOpenFile(file)
    setGotoLine(line ?? null)
    setSource(null); setDeps(null)
    resourceFormalApi.getSource(file).then(setSource).catch(() => setSource(null))
    resourceFormalApi.getDeps(file).then(setDeps).catch(() => setDeps(null))
  }, [])

  const closeSource = useCallback(() => {
    setOpenFile(null); setSource(null); setDeps(null); setGotoLine(null)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      {/* Header */}
      <header className="bg-white/85 backdrop-blur border-b border-qcloud-border px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80">
            <Logo size="small" />
            <span className="text-xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
              QuantumArena
            </span>
          </Link>
          <span className="text-qcloud-border">·</span>
          <span className="text-sm font-semibold text-qcloud-text">Resource Verification</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/resource-verification/tutorial" className="text-sm font-semibold text-qcloud-primary hover:text-qcloud-secondary transition-colors">
            Tutorial: adder ↗
          </Link>
          <Link to="/resource-verification/graph" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Dependency graph</Link>
          <Link to="/arena" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Arena</Link>
          <Link to="/publications" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Publications</Link>
          <AuthHeader />
        </div>
      </header>

      {/* Tight hero */}
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-6 text-center">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-qcloud-primary bg-qcloud-primary/10 rounded-full px-3 py-1 mb-3">
          <span className="w-1.5 h-1.5 rounded-full bg-qcloud-primary animate-pulse" />
          Lean 4 formalization
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-qcloud-text mb-2 leading-tight">
          Resource Verification of Fault-Tolerant Shor
        </h1>
        <p className="text-base md:text-lg italic text-qcloud-muted mb-3">
          Semantic correctness first. Resources as a consequence.
        </p>
        <p className="text-sm text-qcloud-muted max-w-2xl mx-auto mb-4">
          A machine-checkable benchmark across seven state-of-the-art estimates on four hardware families.
        </p>
        <Link
          to="/resource-verification/tutorial"
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-qcloud-primary to-qcloud-secondary text-white hover:opacity-90 shadow-sm"
        >
          New here? Walk through a worked adder example →
        </Link>
      </section>

      <main className="max-w-7xl mx-auto px-6 pb-12 space-y-6">
        {/* Q-Day countdown banner — sets the stakes for everything below */}
        <QDayCountdown variant="full" showCTA={false} />

        {loading && (
          <div className="bg-white rounded-2xl border border-qcloud-border p-12 text-center text-qcloud-muted">Loading…</div>
        )}
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-800">
            <div className="font-semibold mb-1">Couldn't load the audit data</div>
            <div className="text-sm">{error}</div>
          </div>
        )}
        {status && !status.available && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900">
            <div className="font-semibold mb-1">resourceFormal repo not detected</div>
            <div className="text-sm">
              Expected at <code className="font-mono text-xs px-1.5 py-0.5 bg-white rounded border border-amber-200">{status.path}</code>.
            </div>
          </div>
        )}

        <HardwareAbstractionDiagram />
        {framework && <FrameworkDiagram framework={framework} onOpen={openSource} />}
        <Link
          to="/shor"
          className="block bg-white rounded-2xl border border-qcloud-border p-6 hover:border-qcloud-primary transition-colors"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-qcloud-text">Paper results moved to the FT Shor Leaderboard</h2>
              <p className="text-sm text-qcloud-muted mt-1 max-w-3xl">
                Each published estimate's claim beside the figure FormalRV checked for its compiled schedule, with the verification
                chain, trusted inputs and resource-VM evidence for every entry.
              </p>
            </div>
            <span className="text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-qcloud-primary to-qcloud-secondary text-white">Open leaderboard →</span>
          </div>
        </Link>
        {gaps.length > 0 && <CriticalAssumptions gaps={gaps} onOpen={openSource} />}
      </main>

      <SourceSlideOver
        open={!!openFile}
        source={source}
        deps={deps}
        gotoLine={gotoLine}
        onClose={closeSource}
      />
    </div>
  )
}

export default ResourceVerificationPage
